import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import { shortDate } from '../format';
import type { Person, PersonCategory } from '../types';

type PersonForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  birthPlace: string;
  addressLine1: string;
  postalCode: string;
  city: string;
  category: PersonCategory | '';
};

const ROLE_LABELS: Record<string, string> = {
  TENANT: 'locataire',
  LEGAL_REPRESENTATIVE: 'représentant légal',
  GUARANTOR: 'garant',
  PAYER: 'payeur',
};

/** Catégorie fermée, indépendante des rôles de bail : sert avant même tout bail (ex. Aurélie, jamais sur un bail). */
const CATEGORY_LABELS: Record<PersonCategory, string> = {
  TENANT: 'Locataire', GUARANTOR: 'Garant / représentant légal',
  MAINTENANCE_PROVIDER: "Prestataire d'entretien", CONDO_MANAGER: 'Syndic / gestionnaire', OTHER: 'Autre',
};

const EMPTY_FORM: PersonForm = {
  firstName: '', lastName: '', email: '', phone: '', birthDate: '', birthPlace: '',
  addressLine1: '', postalCode: '', city: '', category: '',
};

function toForm(person: Person): PersonForm {
  return {
    firstName: person.firstName,
    lastName: person.lastName,
    email: person.email ?? '',
    phone: person.phone ?? '',
    birthDate: person.birthDate ?? '',
    birthPlace: person.birthPlace ?? '',
    addressLine1: person.addressLine1 ?? '',
    postalCode: person.postalCode ?? '',
    city: person.city ?? '',
    category: person.category ?? '',
  };
}

export function PeoplePanel() {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<'new' | string>('');
  const [form, setForm] = useState<PersonForm>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  async function load(query = search): Promise<void> {
    try {
      const result = await api<{ people: Person[] }>(`/api/admin/people${query ? `?search=${encodeURIComponent(query)}` : ''}`);
      setPeople(result.people);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, []);

  function startCreate(): void {
    setEditingId('new');
    setForm(EMPTY_FORM);
    setError('');
  }

  function startEdit(person: Person): void {
    setEditingId(person.id);
    setForm(toForm(person));
    setError('');
  }

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');
    const body = JSON.stringify({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      birthDate: form.birthDate || null,
      birthPlace: form.birthPlace.trim() || null,
      addressLine1: form.addressLine1.trim(),
      postalCode: form.postalCode.trim(),
      city: form.city.trim(),
      category: form.category || null,
    });
    try {
      if (editingId === 'new') {
        await api('/api/admin/people', { method: 'POST', body });
      } else {
        await api(`/api/admin/people/${editingId}`, { method: 'PATCH', body });
      }
      setEditingId('');
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    if (!window.confirm('Supprimer cette personne ? Impossible si elle est liée à un bail.')) return;
    try {
      await api(`/api/admin/people/${id}`, { method: 'DELETE' });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Suppression impossible.');
    }
  }

  return (
    <section className="panel people-panel">
      <div className="section-heading">
        <div><p className="eyebrow">Répertoire</p><h2>Locataires et contacts</h2></div>
        <button className="button compact" onClick={startCreate}>+ Ajouter</button>
      </div>
      <p className="section-intro">Locataires, représentants légaux, garants et payeurs partagent le même répertoire. Le rôle se fixe au niveau du bail.</p>
      <form className="search-row" onSubmit={(event) => { event.preventDefault(); void load(); }}>
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher un nom ou un e-mail" />
        <button className="button compact secondary">Rechercher</button>
      </form>
      {error && <div className="inline-message error" role="alert">{error}</div>}

      {editingId === 'new' && (
        <form className="form-grid record-form" onSubmit={(event) => void save(event)}>
          <label>Prénom<input value={form.firstName} onChange={(event) => setForm({ ...form, firstName: event.target.value })} required minLength={1} /></label>
          <label>Nom<input value={form.lastName} onChange={(event) => setForm({ ...form, lastName: event.target.value })} required minLength={1} /></label>
          <label>E-mail<input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
          <label>Téléphone<input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
          <label>Né(e) le<input type="date" value={form.birthDate} onChange={(event) => setForm({ ...form, birthDate: event.target.value })} /></label>
          <label>Lieu de naissance<input value={form.birthPlace} onChange={(event) => setForm({ ...form, birthPlace: event.target.value })} /></label>
          <label>Catégorie
            <select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value as PersonCategory | '' })}>
              <option value="">— non catégorisé —</option>
              {(Object.keys(CATEGORY_LABELS) as PersonCategory[]).map((code) => <option key={code} value={code}>{CATEGORY_LABELS[code]}</option>)}
            </select>
          </label>
          <label>Adresse<input value={form.addressLine1} onChange={(event) => setForm({ ...form, addressLine1: event.target.value })} placeholder="12 rue du Taur" /></label>
          <label>Code postal<input value={form.postalCode} onChange={(event) => setForm({ ...form, postalCode: event.target.value })} /></label>
          <label>Ville<input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} /></label>
          <div className="span-2 record-actions">
            <button type="button" className="text-button" onClick={() => setEditingId('')}>Annuler</button>
            <button className="button primary compact" disabled={busy}>{busy ? 'Enregistrement…' : 'Créer'}</button>
          </div>
        </form>
      )}

      {!people && !error && <p className="muted">Chargement…</p>}
      {people?.length === 0 && <div className="empty-state">Aucune personne enregistrée.</div>}
      <div className="record-list">
        {people?.map((person) => (
          <article className="record-card" key={person.id}>
            {editingId === person.id ? (
              <form className="form-grid" onSubmit={(event) => void save(event)}>
                <label>Prénom<input value={form.firstName} onChange={(event) => setForm({ ...form, firstName: event.target.value })} required minLength={1} /></label>
                <label>Nom<input value={form.lastName} onChange={(event) => setForm({ ...form, lastName: event.target.value })} required minLength={1} /></label>
                <label>E-mail<input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
                <label>Téléphone<input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
                <label>Né(e) le<input type="date" value={form.birthDate} onChange={(event) => setForm({ ...form, birthDate: event.target.value })} /></label>
                <label>Lieu de naissance<input value={form.birthPlace} onChange={(event) => setForm({ ...form, birthPlace: event.target.value })} /></label>
          <label>Catégorie
            <select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value as PersonCategory | '' })}>
              <option value="">— non catégorisé —</option>
              {(Object.keys(CATEGORY_LABELS) as PersonCategory[]).map((code) => <option key={code} value={code}>{CATEGORY_LABELS[code]}</option>)}
            </select>
          </label>
          <label>Adresse<input value={form.addressLine1} onChange={(event) => setForm({ ...form, addressLine1: event.target.value })} placeholder="12 rue du Taur" /></label>
          <label>Code postal<input value={form.postalCode} onChange={(event) => setForm({ ...form, postalCode: event.target.value })} /></label>
          <label>Ville<input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} /></label>
                <div className="span-2 record-actions">
                  <button type="button" className="text-button" onClick={() => setEditingId('')}>Annuler</button>
                  <button className="button primary compact" disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
                </div>
              </form>
            ) : (
              <div className="record-heading">
                <div>
                  <strong>{person.firstName} {person.lastName}</strong>
                  {/* Les rôles distinguent deux fiches homonymes : la même
                      personne peut être représentante légale sur un bail et
                      garante sur un autre, sans fusion automatique. */}
                  <span className="person-roles">
                    {person.category && <em className="role-tag category-tag">{CATEGORY_LABELS[person.category]}</em>}
                    {person.roles.length === 0
                      ? <em>aucun bail</em>
                      : person.roles.map((role) => (
                        <em key={role} className="role-tag">{ROLE_LABELS[role] ?? role}</em>
                      ))}
                    {person.birthDate && <span className="record-code">né(e) le {shortDate(person.birthDate)}</span>}
                  </span>
                </div>
                <div className="record-actions">
                  <button className="text-button" onClick={() => startEdit(person)}>Modifier</button>
                  <button className="icon-button danger" aria-label="Supprimer" title="Supprimer" onClick={() => void remove(person.id)}>×</button>
                </div>
              </div>
            )}
            {editingId !== person.id && (person.email || person.phone) && (
              <p className="record-line muted">{[person.email, person.phone].filter(Boolean).join(' · ')}</p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
